import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import {
  ActionBar,
  DataGrid,
  DetailPane,
  Navigator,
  SplitView,
  StatusBar,
  readStored,
  writeStored,
  type DesktopAction,
  type DetailTab,
  type GridColumn,
  type NavNode,
} from '@/components/desktop';
import { HistoryPanel, escapeHtml, openPrintWindow } from '@/components/documents';
import { ErrorNote, OkNote, Pager, SearchBar, Select, money, shortDate } from '@/components/ui';
import {
  FLAG_TEXT,
  FREQUENCY_TEXT,
  PAY_METHOD_TEXT,
  WEEKDAYS,
  WORKER_TYPE_TEXT,
  hrGet,
  useFarmOptions,
  useHrInvalidation,
  type EmployeeDetail,
  type EmployeeRow,
  type PayRevision,
} from '@/hooks/hr';
import { useAuth } from '@/stores/auth';
import { useFarmScope } from '@/stores/farmScope';
import { useScreenIntent, useShellNav, type ScreenIntent } from '@/stores/shellNav';
import { EmployeeDialog, type DialogOutcome, type EmployeeDialogState } from './EmployeeDialogs';
import { EmployeeForm } from './EmployeeForm';
import { EmployeeImport } from './EmployeeImport';
import { ChangePayForm } from './EmployeePay';
import { DetailsTab, FixedItemsTab, PayTab, PersonStatus, PlacementsTab, TimelineTab, type RecurringItem } from './EmployeeTabs';
import './employees.css';

const PAGE_SIZE = 100;
const NODE_KEY = 'poultry.hr.employees.node';

const SHOW: Array<{ value: string; label: string }> = [
  { value: 'CURRENT', label: 'Working now' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'LEFT', label: 'Left' },
];

type View =
  | { kind: 'create' }
  | { kind: 'edit'; id: string }
  | { kind: 'pay'; id: string }
  | { kind: 'import' }
  | null;

type Notice = { tone: 'ok' | 'error'; message: string; settleFor?: string | undefined };

/**
 * The farm filter the API takes for a tree node. The root ("all") is the
 * farms currently in view from the top bar — everyone in scope when it is
 * "All farms", or just those farms together when it has narrowed.
 */
function farmParam(node: string, topFarmIds: readonly string[]): string | undefined {
  if (node === 'HEAD_OFFICE') return 'HEAD_OFFICE';
  if (node.startsWith('farm:')) return node.slice(5);
  if (node === 'all' && topFarmIds.length > 0) return topFarmIds.join(',');
  return undefined;
}

function payCell(r: EmployeeRow): string {
  if (r.workerType === 'DAILY') return r.dailyRate ? `${money(r.dailyRate)}/day` : '—';
  return r.salary ? money(r.salary) : '—';
}

function printProfile(d: EmployeeDetail) {
  const esc = escapeHtml;
  const facts: Array<[string, string | null | undefined]> = [
    ['Code', d.code],
    ['Name', `${d.nameEn}${d.nameUr ? ` / ${d.nameUr}` : ''}`],
    ["Father's or husband's name", d.fatherName],
    ['CNIC', d.cnic],
    ['CNIC expiry', d.cnicExpiry === undefined ? undefined : d.cnicExpiry ? shortDate(d.cnicExpiry) : null],
    ['Date of birth', d.dateOfBirth === undefined ? undefined : d.dateOfBirth ? shortDate(d.dateOfBirth) : null],
    ['Mobile', d.contactNo],
    ['Home address', d.homeAddress],
    ['Status', d.status === 'ACTIVE' ? 'Working' : d.status === 'SUSPENDED' ? 'Suspended' : 'Left'],
    ['Worker type', WORKER_TYPE_TEXT[d.workerType]],
    ['Paid', FREQUENCY_TEXT[d.payFrequency] ?? d.payFrequency],
    ['Department', d.department],
    ['Designation', d.designation],
    ['Farm', d.farmCode ? `${d.farmCode} · ${d.farmName ?? ''}${d.shedName ? ` · ${d.shedName}` : ''}` : 'Head office'],
    ['Reports to', d.reportsTo ? `${d.reportsTo.code} — ${d.reportsTo.nameEn}` : null],
    ['Weekly off', d.weeklyOff.map((x) => WEEKDAYS[x]).join(', ') || 'None'],
    ['Joined', shortDate(d.joinedOn)],
    ['Left', d.leftOn ? shortDate(d.leftOn) : null],
    ['Paid by', PAY_METHOD_TEXT[d.paymentMethod]],
    ['IBAN', d.paymentMethod === 'BANK' ? d.payIban : null],
    ['Wallet number', d.paymentMethod === 'WALLET' ? d.walletNo : null],
    ['EOBI', d.eobiRegistered ? (d.eobiNo ?? 'Registered') : 'Not registered'],
    ['PESSI', d.pessiRegistered ? (d.pessiNo ?? 'Registered') : 'Not registered'],
    ['Income tax', d.taxApplies ? 'Taken from pay' : 'Not taken'],
  ];
  // Anything the server withheld (undefined) is left out of the printout.
  const rows = facts
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v || '—')}</td></tr>`)
    .join('');
  const payLines = d.pay?.lines.filter((l) => l.payAmount) ?? [];
  const pay =
    d.pay && (d.pay.monthlyGross || d.pay.dailyRate)
      ? `<h2>Pay from ${esc(shortDate(d.pay.effectiveFrom))}</h2><table>${payLines
          .map((l) => `<tr><td>${esc(l.nameEn)}</td><td class="num">${esc(money(l.payAmount))}</td></tr>`)
          .join('')}<tr><th>${d.pay.dailyRate ? 'Daily rate' : 'Monthly pay'}</th><th class="num">${esc(
          money(d.pay.dailyRate ?? d.pay.monthlyGross),
        )}</th></tr></table>`
      : '';
  openPrintWindow({
    title: `Profile ${d.code}`,
    body: `<h1>${esc(d.code)} — ${esc(d.nameEn)}${d.nameUr ? ` <span class="urdu" lang="ur">${esc(d.nameUr)}</span>` : ''}</h1>
      <table class="facts">${rows}</table>${pay}
      <div style="display:flex;gap:3rem;margin-top:2.5rem"><span style="border-top:1px solid #111;min-width:11rem;text-align:center;font-size:0.8rem">Employee</span><span style="border-top:1px solid #111;min-width:11rem;text-align:center;font-size:0.8rem">HR / Accounts</span></div>`,
    css: 'table.facts th { width: 14rem; background: #f5f5f5; }',
  });
}

/**
 * Employees: a farm tree, the people on it, and everything about the chosen
 * person below. Pay, farm and status each change through their own action,
 * so every change leaves its own record; pay changes need a second person.
 */
export function Employees() {
  const intent = useScreenIntent('hr-emp');
  const { can } = useAuth();
  const allFarms = useAuth((s) => s.user?.scope.allFarms ?? false);
  const invalidate = useHrInvalidation();
  const farms = useFarmOptions();
  const openRecord = intent?.recordId ?? intent?.employeeId ?? null;

  const [node, setNodeState] = useState<string>(() => (openRecord ? 'all' : readStored(NODE_KEY, 'all')));
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(openRecord ? '' : 'CURRENT');
  const [workerType, setWorkerType] = useState('');
  const [skip, setSkip] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(openRecord);
  const [tab, setTab] = useState('details');
  const [view, setView] = useState<View>(() => (intent?.action === 'create' ? { kind: 'create' } : null));
  const [dialog, setDialog] = useState<EmployeeDialogState | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  // Searching waits for a pause in typing rather than asking on every key.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchText.trim());
      setSkip(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);

  // The tree lists only the farms currently in view from the top bar; a node
  // whose farm has fallen out of view (or head office once the top bar has
  // narrowed to specific farms) falls back to everyone rather than an empty,
  // unexplained grid.
  const topFarmIds = useFarmScope((s) => s.selectedFarmIds);
  const farmList = farms.data ?? [];
  const visibleFarmList = topFarmIds.length === 0 ? farmList : farmList.filter((f) => topFarmIds.includes(f.id));
  const headOfficeVisible = allFarms && topFarmIds.length === 0;
  const nodeFarm = node.startsWith('farm:') ? visibleFarmList.find((f) => f.id === node.slice(5)) : undefined;
  const effectiveNode =
    (node.startsWith('farm:') && farms.isSuccess && !nodeFarm) || (node === 'HEAD_OFFICE' && !headOfficeVisible) ? 'all' : node;
  const farmId = farmParam(effectiveNode, topFarmIds);

  function setNode(next: string) {
    setNodeState(next);
    writeStored(NODE_KEY, next);
    setSelectedKey(null);
    setSkip(0);
    setNotice(null);
  }

  const list = useQuery({
    queryKey: ['hr', 'employees', 'list', farmId, status, workerType, search, skip],
    queryFn: () =>
      hrGet<{ total: number; items: EmployeeRow[] }>('/api/v1/hr/employees', {
        farmId,
        status,
        workerType,
        search,
        skip,
        take: PAGE_SIZE,
      }),
  });
  const rows = list.data?.items ?? [];

  const detail = useQuery({
    queryKey: ['hr', 'employees', 'detail', selectedKey],
    queryFn: () => hrGet<EmployeeDetail>(`/api/v1/hr/employees/${selectedKey}`),
    enabled: selectedKey !== null,
  });
  const d = detail.data && detail.data.id === selectedKey ? detail.data : null;
  const selected: EmployeeRow | null = rows.find((r) => r.id === selectedKey) ?? d;

  const pendingList = useQuery({
    queryKey: ['hr', 'employees', 'pay-changes', 'pending'],
    queryFn: () => hrGet<PayRevision[]>('/api/v1/hr/employees/pay-changes/pending'),
    enabled: can('hr.revision.read'),
  });

  /** The pay change waiting for this person, if any. */
  function pendingFor(id: string): PayRevision | null {
    if (d && d.id === id && d.pendingRevision) return d.pendingRevision;
    return pendingList.data?.find((r) => r.employeeId === id) ?? null;
  }

  const remove = useMutation({
    mutationFn: (row: EmployeeRow) => api.delete(`/api/v1/hr/employees/${row.id}`),
    onSuccess: (_, row) => {
      invalidate();
      setSelectedKey(null);
      setNotice({ tone: 'ok', message: `${row.code} ${row.nameEn} deleted.` });
    },
    onError: (e) => setNotice({ tone: 'error', message: e instanceof Error ? e.message : 'Could not delete them' }),
  });

  function confirmDelete(row: EmployeeRow) {
    if (window.confirm(`Delete ${row.code} ${row.nameEn}? Only someone added by mistake, with no attendance or pay yet, can be deleted.`)) {
      remove.mutate(row);
    }
  }

  function done(outcome: DialogOutcome) {
    invalidate();
    setNotice({ tone: 'ok', message: outcome.message, settleFor: outcome.settleFor });
  }

  function go(screen: string, to?: ScreenIntent) {
    useShellNav.getState().navigate(screen, to);
  }

  function rowActions(row: EmployeeRow): DesktopAction[] {
    const left = row.status === 'LEFT';
    const pending = pendingFor(row.id);
    const update = can('hr.employee.update');
    return [
      {
        label: 'Edit details',
        icon: 'edit',
        shortcut: 'F2',
        onSelect: () => {
          setSelectedKey(row.id);
          setView({ kind: 'edit', id: row.id });
        },
        allowed: update,
      },
      {
        label: 'Change pay',
        icon: 'payroll',
        onSelect: () => {
          setSelectedKey(row.id);
          setView({ kind: 'pay', id: row.id });
        },
        allowed: can('hr.revision.create'),
        enabled: !left && !pending,
        reason: left ? 'They have left' : 'A pay change is already waiting for a decision',
        separatorBefore: true,
      },
      {
        label: 'Approve pay change',
        icon: 'approve',
        onSelect: () => pending && setDialog({ kind: 'approve', row, revision: pending }),
        allowed: can('hr.revision.approve'),
        enabled: !!pending && !pending.raisedByYou,
        reason: pending ? 'Whoever raised a pay change cannot approve it' : 'No pay change is waiting',
      },
      {
        label: 'Reject pay change',
        icon: 'reject',
        onSelect: () => pending && setDialog({ kind: 'reject', row, revision: pending }),
        allowed: can('hr.revision.reject'),
        enabled: !!pending && !pending.raisedByYou,
        reason: pending ? 'Whoever raised a pay change cannot reject it' : 'No pay change is waiting',
      },
      {
        label: 'Move to another farm',
        icon: 'moveFarm',
        onSelect: () => setDialog({ kind: 'transfer', row }),
        allowed: update,
        enabled: !left,
        reason: 'They have left',
        separatorBefore: true,
      },
      row.status === 'SUSPENDED'
        ? {
            label: 'Reinstate',
            icon: 'reactivate',
            onSelect: () => setDialog({ kind: 'reinstate', row }),
            allowed: update,
          }
        : {
            label: 'Suspend',
            icon: 'suspend',
            onSelect: () => setDialog({ kind: 'suspend', row }),
            allowed: update,
            enabled: !left,
            reason: 'They have left',
          },
      left
        ? {
            label: 'Rehire',
            icon: 'joiner',
            onSelect: () => setDialog({ kind: 'rehire', row }),
            allowed: update,
          }
        : {
            label: 'Mark as left',
            icon: 'leaver',
            danger: true,
            onSelect: () => setDialog({ kind: 'left', row }),
            allowed: update,
          },
      {
        label: 'Add fixed monthly item',
        icon: 'add',
        onSelect: () => {
          setSelectedKey(row.id);
          setTab('fixed');
          setDialog({ kind: 'addItem', row });
        },
        allowed: update,
        enabled: !left,
        reason: 'They have left',
        separatorBefore: true,
      },
      {
        label: 'Give advance',
        icon: 'advance',
        onSelect: () => go('hr-adv', { action: 'create', employeeId: row.id }),
        allowed: can('hr.advance.create'),
        enabled: !left,
        reason: 'They have left',
      },
      {
        label: 'Print profile',
        icon: 'print',
        onSelect: () => {
          void hrGet<EmployeeDetail>(`/api/v1/hr/employees/${row.id}`).then(printProfile);
        },
        separatorBefore: true,
      },
      {
        label: 'Delete',
        icon: 'remove',
        danger: true,
        shortcut: 'Del',
        onSelect: () => confirmDelete(row),
        allowed: can('hr.employee.delete'),
      },
    ];
  }

  const toolbar: DesktopAction[] = [
    {
      label: 'Add person',
      icon: 'joiner',
      primary: true,
      onSelect: () => setView({ kind: 'create' }),
      allowed: can('hr.employee.create'),
    },
    {
      label: 'Import from CSV',
      icon: 'importFile',
      onSelect: () => setView({ kind: 'import' }),
      allowed: can('hr.employee.create'),
    },
    ...(selected
      ? rowActions(selected)
      : [
          {
            label: 'Edit details',
            icon: 'edit' as const,
            onSelect: () => {},
            allowed: can('hr.employee.update'),
            enabled: false,
            reason: 'Choose a person first',
            separatorBefore: true,
          },
        ]),
  ];

  const nav: NavNode[] = [
    {
      id: 'all',
      label:
        topFarmIds.length === 0
          ? 'All employees'
          : `All employees (${visibleFarmList.length} farm${visibleFarmList.length === 1 ? '' : 's'})`,
      icon: <AppIcon kind="people" />,
      children: [
        ...visibleFarmList.map((f) => ({
          id: `farm:${f.id}`,
          label: `${f.code} · ${f.nameEn}`,
          icon: <AppIcon kind="farm" />,
        })),
        ...(headOfficeVisible ? [{ id: 'HEAD_OFFICE', label: 'Head office', icon: <AppIcon kind="department" /> }] : []),
      ],
    },
  ];

  const navigator = farms.isError ? (
    <div className="stack">
      <ErrorNote message={farms.error.message} />
    </div>
  ) : (
    <Navigator id="hr-employees" label="Farms" nodes={nav} selectedId={effectiveNode} onSelect={setNode} />
  );

  const nodeLabel = effectiveNode === 'HEAD_OFFICE' ? 'Head office' : nodeFarm ? `${nodeFarm.code} · ${nodeFarm.nameEn}` : 'All employees';

  // A form takes over the right-hand side; the tree stays in view.
  if (view) {
    const close = () => setView(null);
    const saved = (message: string, id?: string) => {
      setView(null);
      if (id) {
        setSelectedKey(id);
        setTab('details');
      }
      done({ message });
    };
    let body;
    if (view.kind === 'create') {
      body = (
        <EmployeeForm
          key="create"
          editing={undefined}
          presetFarmId={effectiveNode === 'HEAD_OFFICE' ? '' : (farmId ?? null)}
          onCancel={close}
          onSaved={saved}
        />
      );
    } else if (view.kind === 'import') {
      body = <EmployeeImport onCancel={close} onDone={(m) => saved(m)} />;
    } else if (!d || d.id !== view.id) {
      body = detail.error ? <ErrorNote message={detail.error.message} /> : <p className="muted">Loading…</p>;
    } else if (view.kind === 'edit') {
      body = <EmployeeForm key={`edit-${d.id}`} editing={d} presetFarmId={null} onCancel={close} onSaved={saved} />;
    } else {
      body = <ChangePayForm key={`pay-${d.id}`} employee={d} onCancel={close} onSaved={(m) => saved(m, d.id)} />;
    }
    return (
      <div className="workbench">
        <SplitView
          id="hr-employees-tree"
          initial={240}
          firstLabel="farm list"
          first={navigator}
          second={<div className="workbench__main workbench__main--form">{body}</div>}
        />
      </div>
    );
  }

  const tabs: DetailTab[] = d
    ? [
        { id: 'details', label: 'Details', icon: 'details', content: <DetailsTab e={d} /> },
        {
          id: 'pay',
          label: 'Pay',
          icon: 'payroll',
          content: (
            <PayTab
              e={d}
              onApprove={(revision) => setDialog({ kind: 'approve', row: d, revision })}
              onReject={(revision) => setDialog({ kind: 'reject', row: d, revision })}
            />
          ),
        },
        {
          id: 'fixed',
          label: 'Fixed items',
          icon: 'cash',
          content: (
            <FixedItemsTab
              e={d}
              canChange={can('hr.employee.update')}
              onAdd={() => setDialog({ kind: 'addItem', row: d })}
              onChange={(item: RecurringItem) => setDialog({ kind: 'changeItem', row: d, item })}
              onEnd={(item: RecurringItem) => setDialog({ kind: 'endItem', row: d, item })}
              onRemove={(item: RecurringItem) => {
                if (window.confirm(`Remove ${item.component}? Use End instead if it was ever paid, so the record stays.`)) {
                  api
                    .delete(`/api/v1/hr/fixed-items/${item.id}`)
                    .then(() => done({ message: `${item.component} removed.` }))
                    .catch((e: unknown) =>
                      setNotice({ tone: 'error', message: e instanceof Error ? e.message : 'Could not remove it' }),
                    );
                }
              }}
            />
          ),
        },
        { id: 'placements', label: 'Placements', icon: 'farm', content: <PlacementsTab e={d} /> },
        { id: 'timeline', label: 'Timeline', icon: 'month', content: <TimelineTab id={d.id} /> },
        { id: 'history', label: 'History', icon: 'history', content: <HistoryPanel path={`/api/v1/hr/employees/${d.id}/history`} /> },
      ]
    : [];

  const links: DesktopAction[] = d
    ? [
        { label: 'Attendance', icon: 'attendance', onSelect: () => go('hr-att'), allowed: can('hr.attendance.read') },
        { label: 'Leave', icon: 'leave', onSelect: () => go('hr-leave', { employeeId: d.id }), allowed: can('hr.leave.read') },
        { label: 'Advances', icon: 'advance', onSelect: () => go('hr-adv', { employeeId: d.id }), allowed: can('hr.advance.read') },
        { label: 'Payslips', icon: 'payslip', onSelect: () => go('hr-pay'), allowed: can('hr.payroll.read') },
      ]
    : [];

  const columns: Array<GridColumn<EmployeeRow>> = [
    {
      key: 'code',
      header: 'Code',
      width: 110,
      render: (r) => (
        <span className="withicon">
          <AppIcon kind="employee" size={14} />
          <span className="mono">{r.code}</span>
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      width: 220,
      render: (r) => (
        <span>
          {r.nameEn}
          {r.nameUr && (
            <>
              {' '}
              <span className="urdu" lang="ur">
                {r.nameUr}
              </span>
            </>
          )}
        </span>
      ),
    },
    { key: 'designation', header: 'Designation', width: 140, render: (r) => r.designation ?? <span className="dim">—</span> },
    { key: 'farm', header: 'Farm', width: 90, render: (r) => <span className="dim">{r.farmCode ?? 'Head office'}</span> },
    { key: 'type', header: 'Worker type', width: 120, render: (r) => WORKER_TYPE_TEXT[r.workerType] },
    { key: 'pay', header: 'Pay', align: 'end', width: 120, render: payCell },
    { key: 'method', header: 'Paid by', width: 110, render: (r) => PAY_METHOD_TEXT[r.paymentMethod] },
    { key: 'status', header: 'Status', width: 120, render: (r) => <PersonStatus status={r.status} /> },
    {
      key: 'attention',
      header: 'Attention',
      width: 220,
      render: (r) => {
        if (r.flags.length === 0) return '';
        const text = r.flags.map((f) => FLAG_TEXT[f] ?? f).join('; ');
        return (
          <span className="withicon employees__attention" title={text}>
            <AppIcon kind="attention" size={14} />
            {text}
          </span>
        );
      },
    },
  ];

  const needAttention = rows.filter((r) => r.flags.length > 0).length;
  const waiting = pendingList.data?.length ?? 0;

  return (
    <div className="workbench">
      <SplitView
        id="hr-employees-tree"
        initial={240}
        firstLabel="farm list"
        first={navigator}
        second={
          <SplitView
            id="hr-employees"
            direction="column"
            initial={400}
            firstLabel="people list"
            first={
              <div className="workbench__main">
                <div className="filterbar">
                  <SearchBar value={searchText} onChange={setSearchText} placeholder="Search by name, code, CNIC or mobile…" />
                  <Select
                    label="Show"
                    value={status}
                    onChange={(v) => {
                      setStatus(v);
                      setSkip(0);
                    }}
                    options={SHOW}
                    placeholder="Everyone"
                  />
                  <Select
                    label="Worker type"
                    value={workerType}
                    onChange={(v) => {
                      setWorkerType(v);
                      setSkip(0);
                    }}
                    options={[
                      { value: 'MONTHLY', label: WORKER_TYPE_TEXT.MONTHLY },
                      { value: 'DAILY', label: WORKER_TYPE_TEXT.DAILY },
                    ]}
                    placeholder="Both"
                  />
                </div>
                <ActionBar actions={toolbar} />
                {notice &&
                  (notice.tone === 'ok' ? (
                    <div className="employees__settle">
                      <OkNote message={notice.message} />
                      {notice.settleFor && can('hr.settlement.create') && (
                        <button
                          type="button"
                          className="btn btn--sm btn--withicon"
                          onClick={() => go('hr-settle', { action: 'create', employeeId: notice.settleFor! })}
                        >
                          <AppIcon kind="settlement" size={15} />
                          Start final settlement
                        </button>
                      )}
                    </div>
                  ) : (
                    <ErrorNote message={notice.message} />
                  ))}
                {list.error && <ErrorNote message={list.error.message} />}
                <DataGrid
                  id="hr-employees"
                  label="People"
                  columns={columns}
                  rows={rows}
                  rowKey={(r) => r.id}
                  selectedKey={selectedKey}
                  onSelect={(k) => {
                    setSelectedKey(k);
                    if (notice?.tone === 'error') setNotice(null);
                  }}
                  onOpen={(r) => {
                    setSelectedKey(r.id);
                    setTab('details');
                  }}
                  onEdit={(r) => {
                    if (can('hr.employee.update')) {
                      setSelectedKey(r.id);
                      setView({ kind: 'edit', id: r.id });
                    }
                  }}
                  onDelete={(r) => {
                    if (can('hr.employee.delete')) confirmDelete(r);
                  }}
                  actions={rowActions}
                  loading={list.isPending}
                  empty={search ? 'Nobody matches your search.' : 'Nobody here yet.'}
                />
                <Pager skip={skip} take={PAGE_SIZE} total={list.data?.total ?? 0} onChange={setSkip} />
              </div>
            }
            second={
              <DetailPane
                tabs={tabs}
                activeTab={tab}
                onTabChange={setTab}
                actions={links}
                empty={selectedKey && detail.isPending ? 'Loading…' : 'Choose a person to see their details, pay and history.'}
                title={
                  selected && (
                    <span className="withicon">
                      <AppIcon kind="employee" size={14} />
                      <span className="mono">{selected.code}</span> {selected.nameEn}
                      {selected.nameUr && (
                        <span className="urdu" lang="ur">
                          {selected.nameUr}
                        </span>
                      )}
                    </span>
                  )
                }
              />
            }
          />
        }
      />
      <StatusBar>
        <span className="withicon">
          <AppIcon kind={effectiveNode === 'all' ? 'people' : effectiveNode === 'HEAD_OFFICE' ? 'department' : 'farm'} size={14} />
          {nodeLabel}
        </span>
        <span>
          {list.data?.total ?? 0} {list.data?.total === 1 ? 'person' : 'people'}
        </span>
        {needAttention > 0 && <span>{needAttention} need attention</span>}
        {waiting > 0 && <span>{waiting} pay {waiting === 1 ? 'change' : 'changes'} waiting</span>}
        <span>{selected ? `Chosen: ${selected.code}` : 'Nobody chosen'}</span>
      </StatusBar>

      {dialog && <EmployeeDialog dialog={dialog} onClose={() => setDialog(null)} onDone={done} />}
    </div>
  );
}
